import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../pages/Auth';

// Types
export type SubscriptionTier = 'free' | 'pro' | 'lifetime';
export type ExportFormat = 'png' | 'svg' | 'pdf';

export interface SubscriptionInfo {
  tier: SubscriptionTier;
  isPaid: boolean;
  diagrams: {
    current: number;
    max: number;
    remaining: number;
    canCreate: boolean;
  };
  exportFormats: ExportFormat[];
  subscriptionExpiresAt?: string | null;
  isCanceling?: boolean;
}

// Tier limits (mirrored from backend for instant checks — MUST match
// apps/backend/src/lib/subscription.ts, which is authoritative. Free is png-only;
// SVG is Pro. A mismatch here previously offered free users SVG export that the
// backend then 403'd.)
const TIER_LIMITS = {
  free: {
    maxDiagrams: 10,
    exportFormats: ['png'] as ExportFormat[],
  },
  pro: {
    maxDiagrams: Infinity,
    exportFormats: ['png', 'svg'] as ExportFormat[],
  },
  lifetime: {
    maxDiagrams: Infinity,
    exportFormats: ['png', 'svg'] as ExportFormat[],
  },
} as const;

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000';

/**
 * Helper to get effective tier considering expiration
 */
function getEffectiveTier(
  rawTier: SubscriptionTier, 
  subscriptionExpiresAt?: string | null
): SubscriptionTier {
  if (rawTier === 'pro' && subscriptionExpiresAt) {
    const expiresAt = new Date(subscriptionExpiresAt);
    if (expiresAt < new Date()) {
      return 'free';
    }
  }
  return rawTier;
}

export function useSubscription() {
  const { user, isAuthenticated } = useAuth();
  const [subscriptionInfo, setSubscriptionInfo] = useState<SubscriptionInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Get raw tier and expiry from user object
  const userData = user as { subscriptionTier?: string; subscriptionExpiresAt?: string } | null;
  const rawTier: SubscriptionTier = (userData?.subscriptionTier as SubscriptionTier) || 'free';
  const subscriptionExpiresAt = userData?.subscriptionExpiresAt || null;
  
  // Calculate effective tier (respects expiration)
  const tier = getEffectiveTier(rawTier, subscriptionExpiresAt);
  const isPaidUser = tier === 'pro' || tier === 'lifetime';
  const limits = TIER_LIMITS[tier] || TIER_LIMITS.free;
  
  // Check if subscription is canceling (has expiry date in the future)
  const isCanceling = rawTier === 'pro' && subscriptionExpiresAt 
    ? new Date(subscriptionExpiresAt) > new Date() 
    : false;

  // Fetch detailed subscription info from API
  const fetchSubscriptionInfo = useCallback(async () => {
    if (!isAuthenticated) {
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/diagrams/subscription`, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to fetch subscription info');
      }

      const data = await response.json();
      setSubscriptionInfo(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching subscription info:', err);
      setError('Failed to load subscription info');
      // Set default values based on user tier
      const currentTier = tier;
      const currentLimits = TIER_LIMITS[currentTier] || TIER_LIMITS.free;
      setSubscriptionInfo({
        tier: currentTier,
        isPaid: currentTier === 'pro' || currentTier === 'lifetime',
        diagrams: {
          current: 0,
          max: currentLimits.maxDiagrams,
          remaining: currentLimits.maxDiagrams,
          canCreate: true,
        },
        exportFormats: currentLimits.exportFormats as ExportFormat[],
      });
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, tier]); // Removed isPaidUser and limits to prevent circular updates

  // Fetch on mount and when auth state changes (not on every user object change)
  useEffect(() => {
    fetchSubscriptionInfo();
    // Intentionally runs only on auth-state change. fetchSubscriptionInfo
    // changes with tier, so depending on it would refetch in a loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Check if user can create a diagram
  const canCreateDiagram = subscriptionInfo?.diagrams.canCreate ?? true;

  // Check if user can export in a specific format
  const canExportFormat = useCallback((format: ExportFormat): boolean => {
    if (isPaidUser) return true;
    return limits.exportFormats.includes(format);
  }, [isPaidUser, limits.exportFormats]);

  // Get diagram usage string (e.g., "3/5")
  const diagramUsageString = subscriptionInfo 
    ? subscriptionInfo.diagrams.max === Infinity
      ? `${subscriptionInfo.diagrams.current} diagrams`
      : `${subscriptionInfo.diagrams.current}/${subscriptionInfo.diagrams.max}`
    : '';

  // Refresh subscription info (call after creating/deleting diagrams)
  const refresh = useCallback(() => {
    setIsLoading(true);
    return fetchSubscriptionInfo();
  }, [fetchSubscriptionInfo]);

  return {
    // Tier info
    tier,
    isPaidUser,
    isFreeTier: tier === 'free',
    isCanceling,
    subscriptionExpiresAt,
    
    // Detailed info (from API)
    subscriptionInfo,
    
    // Diagram limits
    canCreateDiagram,
    diagramCount: subscriptionInfo?.diagrams.current ?? 0,
    maxDiagrams: subscriptionInfo?.diagrams.max ?? limits.maxDiagrams,
    remainingDiagrams: subscriptionInfo?.diagrams.remaining ?? limits.maxDiagrams,
    diagramUsageString,
    
    // Export limits
    canExportFormat,
    allowedExportFormats: limits.exportFormats,
    
    // State
    isLoading,
    error,
    
    // Actions
    refresh,
  };
}

export default useSubscription;

